import {
  Button,
  Col,
  Flex,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  Card,
} from "antd";
import React, { useState } from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SearchOutlined,MessageOutlined } from "@ant-design/icons";
import { dateObjToDispViewDate } from "../../../../../utils/utils";
import { colors } from "../../../../../constants/colors";
import {
  getTicketsAction,
  startSupportTicketChatConversationAction,
  updateTicketStatusAction,
} from "../../../../../redux/services/ticket-management/ticketManagementServices";
import Spinner from "../../../../common/spinner/Spinner";
import { ChatsIcon, InfoButtonIcon } from "../../../../../assets/svg";
import useDebounce from "../../../../../hooks/useDebounce";
import { appConstConfig } from "../../../../../constants/config/appConstConfig";
import Actions from "../../../../../redux/actions";
import {
  baseRouteMakerForSuperAdmin,
  routes,
} from "../../../../../constants/routeConsts";
import styles from "./TicketManagement.module.scss";
import { userRoles } from "../../../../../constants";
import { toastService } from "../../../../common/toast-messages/toast";

const TicketManagement = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* For navigating between pages */
  const navigate = useNavigate();
  /* For navigating between pages */

  /* form dependencies */
  const [form] = Form.useForm();
  /* form dependencies */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  const [selectListLoader, setSelectListLoader] = useState(false);
  /* loading dependencies */

  /* table dependencies */
  const [columns, setColumns] = useState([]);
  const { tickets } = useSelector((state) => state.tickets);
  const [searchValue, setSearchValue] = useState("");
  const [pagination, setPagination] = useState({
    pageNo: appConstConfig.DEFAULT_TABLE_PAGE_NUMBER,
    pageSize: appConstConfig.DEFAULT_TABLE_PAGE_SIZE,
  });
  const paginationObj = {
    total: tickets?.total,
    current: pagination?.pageNo,
    pageSize: pagination?.pageSize,
    onChange: (pageNo, pageSize) => setPagination({ pageNo, pageSize }),
    pageSizeOptions: appConstConfig.DEFAULT_PAGE_SIZE_OPTIONS_ARR,
    showSizeChanger: true,
  };
  /* table dependencies */

  /* filter dependencies */
  const { debouncedValue: debouncedSearchValue, debounceLoading } = useDebounce(
    searchValue,
    500
  );
  const [selectedFilterStatus, setSelectedFilterStatus] = useState("All");
  const roleArr = [
    { value: userRoles.SERVICE_PROVIDER_ADMIN, label: "Service Provider" },
    { value: userRoles.CUSTOMER, label: "User" },
  ];
  const [currentRole, setCurrentRole] = useState(null);
  /* filter dependencies */

  const { loginDetails } = useSelector((state) => state.auth);

  /* function definition to get table data */
  const handleGetTableData = (tempPageNo, tempPageSize, isUpdateStatus = false) => {
    dispatch(
      Actions.createAction(Actions.GET_TICKET_MANAGEMENT_LIST, { tickets: {} })
    );
    const dataToBeSent = {
      page: debouncedSearchValue !== "" ? 1 : isUpdateStatus ? tempPageNo : pagination?.pageNo,
      limit: isUpdateStatus ? tempPageSize : pagination?.pageSize,
      search: debouncedSearchValue,
      status: selectedFilterStatus === "All" ? "" : selectedFilterStatus,
      role: currentRole?.value,
    };
    dispatch(getTicketsAction(dataToBeSent, setLoading));
  };
  /* function definition to get table data */

  useEffect(() => {
    handleGetTableData();
  }, [pagination, debouncedSearchValue, selectedFilterStatus, currentRole]);

  /* on status changed */
  const onStatusChanged = async (id, value, record) => {
    const dataToBeSent = {};
    dataToBeSent.selectedTicketId = record?._id;
    dataToBeSent["status"] = value?.value;

    try {
      await dispatch(updateTicketStatusAction(dataToBeSent, setLoading))
      handleGetTableData(pagination.pageNo,pagination.pageSize, true)
      setSelectedFilterStatus("All");

    } catch (error) {
      toastService.error(error)
    }
  };
  /* on status changed */

  /* initial dependencies rendering */
  useEffect(() => {
    /* table column config */
    const tempTableColumns = [
      {
        title: t("chats.ticketNumber"),
        dataIndex: "ticket_number",
        key: "ticket_number",
        render: (_, record) => <p>{record?.ticket_number || "-"}</p>,
      },
      {
        title: t("dateofRequest"),
        dataIndex: "created_at",
        key: "created_at",
        render: (_, record) => (
          <p>{dateObjToDispViewDate(record?.created_at, "ddmmyyyy")}</p>
        ),
      },
      {
        title: t("chats.userName"),
        dataIndex: "name",
        key: "name",
      },
      {
        title: t("chats.userEmail"),
        dataIndex: "email",
        key: "email",
      },
      {
        title: t("Modules.ServiceProviderAuth.details"),
        key: "action",
        align: "center",
        render: (_, record) => {
          return (
            <>
              <Typography.Link onClick={(e) => handleViewDetails(e, record)}>
                View
              </Typography.Link>
            </>
          );
        },
      },

      {
        title: t("Modules.AutoCredit.status"),
        key: "action",
        align: "center",
        render: (_, record) => {
          const getColorBasedOnStatus = (status) => {
            switch (status) {
              case "Pending":
                return { background: colors.lightPeach, color: colors.salmon };

              case "Open":
                return {
                  background: colors.powderBlue,
                  color: colors.oceanBlue,
                };

              case "Closed":
                return {
                  background: colors.lightPinkishRed,
                  color: colors.darkMaroon,
                };

              default:
                break;
            }
          };

          const getTicketStatusName = (status) => {
            switch (status) {
              case "Pending":
                return t("CommonUtils.pending")

              case "Open":
                return t("CommonUtils.open")

              case "Closed":
                return t("CommonUtils.closed")

              default:
                break;
            }
          }
          return (
            <>
              <Tag
                bordered={false}
                style={getColorBasedOnStatus(record?.status)}
              >
                {getTicketStatusName(record?.status)}
              </Tag>
            </>
          );
        },
      },
      {
        title: t("appointmentInfo.action"),
        key: "action",
        align: "center",
        render: (_, record) => {
          const getTicketStatusName = (status) => {
            switch (status) {
              case "Pending":
                return t("CommonUtils.pending")

              case "Open":
                return t("CommonUtils.open")

              case "Closed":
                return t("CommonUtils.closed")

              default:
                break;
            }
          }

          const tempDefaultValue = {
            value: record?.status,
            label: getTicketStatusName(record?.status),
          };

          return (
            <>
              <Select
                options={[
                  { label: t("CommonUtils.pending"), value: "Pending" },
                  { label: t("CommonUtils.open"), value: "Open" },
                  { label: t("CommonUtils.closed"), value: "Closed" },
                ]}
                size="small"
                placeholder={t("Modules.User.selectStatus")}
                value={tempDefaultValue}
                style={{ width: 120 }}
                onChange={(id, value) => onStatusChanged(id, value, record)}
              />
            </>
          );
        },
      },
      {
        title: (
          <>
            <Flex gap="1rem" align="center" justify="center">
              <>{t("chats.chats")}</>
              <Tooltip
                placement="topLeft"
                title={t("Modules.TicketManagement.chatsTooltipInfo")}
                color={colors.DarkMint}
              >
                <Button type="text" shape="circle" size="small">
                  <Flex align="center" justify="center">
                    <InfoButtonIcon />
                  </Flex>
                </Button>
              </Tooltip>
            </Flex>
          </>
        ),
        key: "action",
        align: "center",
        render: (_, record) => {
          return (
            <>
              <Button
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "auto",
                }}
                type="dashed"
                size="medium"
                icon={<ChatsIcon fillColor={colors.primaryColor} />}
                onClick={(e) => handleChat(e, record)}
              />
            </>
          );
        },
      },
    ];
    /* table column config */
    setColumns(tempTableColumns);
  }, [tickets?.data]);
  /* initial dependencies rendering */

  const handleViewDetails = (e, row) => {
    dispatch(
      Actions.createAction(Actions.SELECTED_TICKET_MANAGEMENT_ID, {
        selectedTicketManagementId: "",
      })
    );
    navigate(baseRouteMakerForSuperAdmin(routes.TICKET_MANAGEMENT_DETAILS));
    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      const selectedTicketManagementId = row?._id;
      dispatch(
        Actions.createAction(Actions.SELECTED_TICKET_MANAGEMENT_ID, {
          selectedTicketManagementId,
        })
      );
    }
  };

  const handleChat = (e, row) => {
    dispatch(
      Actions.createAction(
        Actions.SELECTED_TICKET_MANAGEMENT_CHAT_ID_DETAILS,
        {}
      )
    );

    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      if (
        !row?.chat_conversation_id ||
        row?.chat_conversation_id === "" ||
        row?.chat_conversation_id === null ||
        row?.chat_conversation_id === undefined
      ) {
        const dataToBeSent = {
          to_id: row?.user_id,
          selectedTicketManagementId: row?._id,
        };
        dispatch(
          startSupportTicketChatConversationAction(dataToBeSent, setLoading)
        ).then((apiRes) => {
          dispatch(
            Actions.createAction(
              Actions.SELECTED_TICKET_MANAGEMENT_CHAT_ID_DETAILS,
              {
                selectedTicketManagementChatIdDetails: {
                  selectedChatConversationId: apiRes?.conversation?._id,
                },
              }
            )
          );
          navigate(baseRouteMakerForSuperAdmin(routes.TICKET_MANAGEMENT_CHAT));
        });
      } else {
        dispatch(
          Actions.createAction(
            Actions.SELECTED_TICKET_MANAGEMENT_CHAT_ID_DETAILS,
            {
              selectedTicketManagementChatIdDetails: {
                selectedChatConversationId: row?.chat_conversation_id,
              },
            }
          )
        );
        navigate(baseRouteMakerForSuperAdmin(routes.TICKET_MANAGEMENT_CHAT));
      }
    }
  };

  return (
    <div className={styles.ticketManagementContainer}>
      <Flex vertical gap="2rem">
        <Row gutter={[24]}>
        <Card
                  bordered
                  hoverable
                  style={{
                    width: 300,
                    padding: 0,
                    borderRadius: 20,
                    border: `1px solid ${colors.oceanBlue}`,
                  }}
                >
              <Row gutter={[24, 24]} align="middle" justify="center">
                <Col span={6}>
                  <Button
                    type="primary"
                    shape="circle"
                    size="middle"
                    icon={<MessageOutlined style={{ fontSize: 22 }} />}
                    style={{ background: colors.oceanBlue }}
                  />
                </Col>
                <Col span={18}>
                  <Flex vertical>
                    <Typography.Title level={3}>
                      {t("Modules.TicketManagement.supportTickets")}
                    </Typography.Title>
                    <Typography.Title
                      level={2}
                      style={{ margin: 0, color: colors.DarkMint }}
                    >
                      {`${tickets?.total || 0}`}
                    </Typography.Title>
                  </Flex>
                </Col>
              </Row>
            </Card>
        </Row>
        <Row gutter={[24]}>
          <Col span={7} style={{ display: "flex", alignItems: "end" }}>
            <Input
              size="large"
              suffix={<SearchOutlined />}
              placeholder={t("CommonUtils.searchByNameEmail")}
              onChange={(event) => setSearchValue(event.target.value)}
            />
          </Col>
          <Col span={7} style={{ display: "flex", alignItems: "center" }}>
            <Select
              style={{ width: "370px" }}
              loading={selectListLoader}
              options={roleArr}
              placeholder={t("Modules.Configuration.selectUserType")}
              onChange={(value) => {
                const selectedRoleObj = roleArr?.find(
                  (element) => element.value === value
                );
                setCurrentRole(selectedRoleObj);
              }}
              value={currentRole}
              allowClear
            />
          </Col>
          <Col
            span={9}
            style={{
              display: "flex",
              alignItems: "end",
              justifyContent: "center",
            }}
          >
            <Radio.Group
              onChange={(event) => {
                setSelectedFilterStatus(event.target.value);
                setPagination((prevValue) => {
                  return {
                    pageNo: 1,
                    pageSize: prevValue?.pageSize,
                  };
                });
              }}
              value={selectedFilterStatus}
              options={[
                { value: "All", label: t("CommonUtils.all") },
                { value: "Pending", label: t("CommonUtils.pending")},
                { value: "Open", label: t("CommonUtils.open")},
                { value: "Closed", label: t("CommonUtils.closed")},
              ]}
            />
          </Col>
        </Row>
        {loading || debounceLoading ? (
          <Spinner />
        ) : (
          <Table
            dataSource={tickets?.data}
            columns={columns}
            pagination={paginationObj}
          />
        )}
      </Flex>
    </div>
  );
};

export default TicketManagement;
